import React from 'react';
import './BilanNeuropsychologique.scss';

/**
 * Page expliquant la démarche et le déroulement en étapes du Bilan Neuropsychologique.
 */
const BilanNeuropsychologique = () => {
  const steps = [
    {
      number: 1,
      title: "Entretien d’anamnèse",
      duration: "50 min",
      description: "Le premier rendez-vous est un temps de rencontre permettant de recueillir des informations afin de comprendre votre demande, votre parcours, vos difficultés et leurs répercussions dans votre vie quotidienne. Cet entretien permet d'orienter l'évaluation en fonction de vos besoins.",
      note: "‼️ Pensez à apporter les documents qui peuvent fournir des informations complémentaires (carnet de santé, bulletins scolaires, comptes-rendus d'examens)."
    },
    {
      number: 2,
      title: "Évaluation psychométrique",
      duration: "Entre 1h30 et 3h30",
      description: "L'évaluation consiste en la passation de tests standardisés et de questionnaires adaptés à votre situation. Ils permettent d'explorer différentes fonctions cognitives (mémoire, attention, langage, raisonnement, etc.).",
      note: "‼️ La durée et le nombre de séances proposées sont variables. L’évaluation peut également porter sur l’efficience intellectuelle (QI)."
    },
    {
      number: 3,
      title: "Analyse et rédaction",
      duration: "1 à 2 semaines",
      description: "Les résultats sont analysés et interprétés selon des normes standardisées. Ils permettent de mettre en évidence vos ressources, vos difficultés et leur retentissement dans votre quotidien.",
      note: "Un compte rendu détaillé est rédigé et reprend l'ensemble des conclusions ainsi que les préconisations."
    },
    {
      number: 4,
      title: "Restitution et recommandations",
      duration: "50 min",
      description: "Un dernier rendez-vous est consacré à l'explication des résultats, dans un langage clair et accessible.",
      note: "Des recommandations personnalisées vous sont proposées afin de vous accompagner au mieux dans votre quotidien."
    }
  ];

  return (
    <div className="bilan-page container">
      <header className="page-header">
        <h1>Le bilan neuropsychologique</h1>
        <p className="subtitle">Comprendre votre fonctionnement cognitif pour mieux avancer</p>
      </header>

      {/* Cartes des Démarches */}
      <section className="demarches-section">
        <h2>Le bilan neuropsychologique peut s’inscrire dans différentes démarches :</h2>
        
        <div className="demarches-grid">
          <div className="demarche-card">
            <h3>La démarche diagnostique</h3>
            <p>
              Le bilan neuropsychologique permet de formuler des <strong>hypothèses</strong> et contribue à <strong>l’élaboration d’un diagnostic</strong> (par exemple un TDA/H, une maladie de Parkinson, HPI, etc).
            </p>
            <span className="medical-note">Toutefois, seul un médecin est habilité à poser un diagnostic.</span>
          </div>

          <div className="demarche-card">
            <h3>La démarche exploratoire</h3>
            <p>
              Le bilan neuropsychologique permet <strong>d’évaluer les difficultés</strong> rencontrées dans la vie quotidienne, professionnelle ou universitaire (mémoire, attention, organisation).
            </p>
            <p>
              Il permet également <strong>d’évaluer les conséquences cérébrales</strong> dans le cadre d’une maladie neurologique, 
              de l’avancée en âge ou suite à un traumatisme crânien ou un AVC.
            </p>
          </div>

          <div className="demarche-card">
            <h3>La démarche évolutive</h3>
            <p>
              Le bilan neuropsychologique permet de faire un point sur <strong>l’évolution</strong> du patient depuis sa 
              dernière évaluation (progression, stagnation, dégradation, etc.).
            </p>
          </div>
        </div>

        <div className="info-banner">
          <p>
            💡 <strong>Bon à savoir :</strong> Il est fréquent qu’un bilan soit suggéré par un médecin ou un spécialiste 
            (neurologue, psychiatre, orthophoniste, ...). Cependant, <strong>aucune prescription médicale n’est nécessaire</strong>. 
            Vous pouvez prendre rendez-vous directement.
          </p>
        </div>
      </section>

      {/* Processus des Bulles Reliées par des flèches */}
      <section className="process-section">
        <h2>Comment se déroule un bilan neuropsychologique ?</h2>

        <div className="steps-wrapper">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="step-card">
                <div className="step-bubble">
                  <span className="step-number">{step.number}</span>
                </div>
                <h3 className="step-title">{step.title}</h3>
                <span className="step-duration">{step.duration}</span>
                <p className="step-desc">{step.description}</p>
                <p className="step-note">{step.note}</p>
              </div>

              {/* Flèche entre les bulles sauf pour la dernière */}
              {index < steps.length - 1 && (
                <div className="step-arrow" aria-hidden="true">
                  ➔
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>
    </div>
  );
};

export default BilanNeuropsychologique;